/* eslint-disable @typescript-eslint/ban-ts-comment */

'use client';

import * as React from 'react';
import {
  ColumnDef,
  RowSelectionState,
  SortingState,
  flexRender,
  getCoreRowModel,
  getFacetedRowModel,
  getFacetedUniqueValues,
  getPaginationRowModel,
  useReactTable,
} from '@tanstack/react-table';

import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';

import Pagination from '@/components/commons/pagination';
interface DataTableProps<TData, TValue> {
  columns: ColumnDef<TData, TValue>[];
  data: TData[];
  totalCount: number;
  className?: string;
  enablePagination?: boolean;
  wrapperClassName?: string;
  isCustomSize?: boolean;
  isLoading?: boolean;
  pagination?: { pageIndex: number; pageSize: number };
  sorting?: SortingState;
  onPageChange?: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
  onSortingChange?: (sort: SortingState) => void;
  onRowSelectionChange?: (selection: RowSelectionState) => void;
  resetRowSelection?: boolean;
  actionOptions?: {
    label?: string;
    tooltip?: string;
    icon?: React.ReactNode;
    variation: 'default' | 'destructive' | 'outline' | 'secondary' | 'ghost' | 'link';
    className: string;
    onClick: () => void;
  }[];
  isDisabled?: boolean;
  usePaginationRange?: boolean;
  idProps?: string;
  initialRowSelection?: RowSelectionState;
}
export default function DataTable<TData, TValue>({
  columns,
  data,
  totalCount,
  className,
  wrapperClassName,
  isLoading,
  isCustomSize,
  pagination = { pageIndex: 0, pageSize: 50 },
  sorting = [],
  onPageChange,
  onPageSizeChange,
  onRowSelectionChange = () => {},
  resetRowSelection,
  actionOptions,
  isDisabled,
  enablePagination = true,
  usePaginationRange = true,
  idProps = 'id',
  initialRowSelection = {},
}: DataTableProps<TData, TValue>) {
  const [rowSelection, setRowSelection] = React.useState<RowSelectionState>(initialRowSelection);

  const table = useReactTable({
    data,
    columns,
    initialState: {
      pagination: { pageIndex: 0, pageSize: 50 },
    },
    state: {
      sorting,
      rowSelection,
    },
    enableRowSelection: true,
    // @ts-ignore
    getRowId: (row, index) => row[idProps] || index,
    onRowSelectionChange: setRowSelection,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getFacetedRowModel: getFacetedRowModel(),
    getFacetedUniqueValues: getFacetedUniqueValues(),
  });

  const hasRowSelection = Object.keys(rowSelection).length > 0;
  const selectedCount = Object.keys(rowSelection).length;

  React.useEffect(() => {
    onRowSelectionChange(rowSelection);
  }, [rowSelection]);

  React.useEffect(() => {
    if (resetRowSelection) {
      setRowSelection({});
    }
  }, [resetRowSelection, data]);

  const isDataEmpty = data.length === 0 && !isLoading;

  return (
    <div className={cn({ 'pointer-events-none opacity-50': isDisabled })}>
      <div
        className={cn(
          'mb-4 h-[calc(100vh-320px)] overflow-auto rounded-md border p-0',
          wrapperClassName
        )}
      >
        <Table className={cn(isCustomSize && 'w-max', className)}>
          <TableHeader
            className='tr-sm sticky top-0 z-10 rounded rounded-tl-sm transition-all duration-300 ease-in-out'
            style={{
              background: hasRowSelection
                ? 'linear-gradient(0deg, rgba(255, 255, 255, 0.94) 0%, rgba(255, 255, 255, 0.94) 100%), #002aff'
                : 'linear-gradient(0deg, rgba(255, 255, 255, 0.94) 0%, rgba(255, 255, 255, 0.94) 100%), #4DACE0',
            }}
          >
            <TableRow>
              {table.getHeaderGroups().map((headerGroup) => (
                <React.Fragment key={headerGroup.id}>
                  {headerGroup.headers.map((header) => (
                    <TableHead
                      key={header.id}
                      className={cn(`transition-all duration-300 ease-in-out`, {
                        hidden: hasRowSelection && header.id !== 'select',
                        'w-[50px]': header.id === 'select', // Set width to 50px if header.id is 'select'
                        [`w-[${header.getSize()}px]`]: isCustomSize && header.id !== 'select', // Apply custom size if not 'select'
                      })}
                    >
                      {header.isPlaceholder
                        ? null
                        : flexRender(header.column.columnDef.header, header.getContext())}
                    </TableHead>
                  ))}
                  {hasRowSelection && (
                    <>
                      <TableHead
                        colSpan={columns.length}
                        className='h-12 text-start transition-all duration-300 ease-in-out'
                      >
                        <div className='flex items-center'>
                          <span className='text-sm font-medium'>
                            {selectedCount} item(s) selected
                          </span>
                          <div className='mx-4 flex space-x-0.5'>
                            <TooltipProvider>
                              {actionOptions?.map((action, index) => (
                                <Tooltip key={index}>
                                  <TooltipTrigger asChild>
                                    <Button
                                      variant={action.variation}
                                      size='sm'
                                      data-testid='action-datatable'
                                      className={cn(
                                        'hover:-translate-y-0. flex transform items-center transition-transform duration-300 ease-in-out',
                                        action.className
                                      )}
                                      onClick={action.onClick}
                                      disabled={isDisabled}
                                    >
                                      {action.icon && (
                                        <span className='flex-shrink-0'>{action.icon}</span>
                                      )}
                                      {action.label && <span>{action.label}</span>}
                                    </Button>
                                  </TooltipTrigger>
                                  <TooltipContent className='rounded-md bg-gray-800'>
                                    <p className='text-xs'>{action.tooltip}</p>
                                  </TooltipContent>
                                </Tooltip>
                              ))}
                            </TooltipProvider>
                          </div>
                        </div>
                      </TableHead>
                    </>
                  )}
                </React.Fragment>
              ))}
            </TableRow>
          </TableHeader>
          <TableBody>
            {table.getRowModel().rows.length ? (
              table.getRowModel().rows.map((row) => (
                <TableRow key={row.id} data-state={row.getIsSelected() && 'selected'}>
                  {row.getVisibleCells().map((cell) => (
                    <TableCell key={cell.id} style={{ width: `${cell.column.getSize()}px` }}>
                      {flexRender(cell.column.columnDef.cell, cell.getContext())}
                    </TableCell>
                  ))}
                </TableRow>
              ))
            ) : isDataEmpty ? (
              <TableRow>
                <TableCell colSpan={columns.length} className='h-24 text-center'>
                  No results.
                </TableCell>
              </TableRow>
            ) : null}
          </TableBody>
        </Table>
      </div>
      {enablePagination && (
        <Pagination
          currentPage={pagination.pageIndex + 1}
          totalCount={totalCount}
          pageSize={pagination.pageSize}
          onPageChange={(page: number) => {
            onPageChange?.(page);
            table.setPageIndex(page - 1);
          }}
          onPageSizeChange={(pageSize: number) => {
            onPageSizeChange?.(pageSize);
            table.setPageSize(pageSize);
          }}
          usePaginationRange={usePaginationRange}
        />
      )}
    </div>
  );
}
